import { datadogRum } from "@datadog/browser-rum";
import { IntlShape } from "react-intl";

import {
  FSVDrinkOptions,
  FSVFoodPackages,
  FSVRoomTypes,
} from "~/types/api-types";

import { getFoodDrinkLabel } from "./fsv-food-drink-category.utils";

/**
 * Marks a switch `default` as unreachable when every union member is handled.
 * If the union grows without a new `case`, TypeScript errors here.
 */
function assertNever(value: never): string {
  datadogRum.addError(new Error(`Unhandled FSV option: ${String(value)}`));
  return "";
}

const getRoomTypeWithDurationMessage = (
  roomType: FSVRoomTypes,
  duration: number,
  intl: IntlShape,
): string => {
  switch (roomType) {
    case "full_space_buyout":
      return intl.formatMessage(
        {
          defaultMessage: "Full space for {duration} hrs",
        },
        { duration },
      );
    case "private_room":
      return intl.formatMessage(
        {
          defaultMessage: "Private room for {duration} hrs",
        },
        { duration },
      );
    case "semi_private_area":
      return intl.formatMessage(
        {
          defaultMessage: "Semi-private area for {duration} hrs",
        },
        { duration },
      );
    case "reserved_area":
      return intl.formatMessage(
        {
          defaultMessage: "Reserved area for {duration} hrs",
        },
        { duration },
      );
    default:
      return "";
  }
};

const getDrinkOptionMessage = (
  drinkOption: FSVDrinkOptions | undefined,
  intl: IntlShape,
): string => {
  switch (drinkOption) {
    case "full_bar":
      return intl.formatMessage({
        defaultMessage: "Cocktails, wine, and beer menu",
      });
    case "beer_and_wine_only":
      return intl.formatMessage({
        defaultMessage: "Wine and beer menu",
      });
    case "non_alcoholic_options_only":
      return intl.formatMessage({
        defaultMessage: "Non-alcoholic menu",
      });
    case "outside_drinks_allowed":
      return intl.formatMessage({
        defaultMessage: "Outside drinks allowed",
      });
    default:
      return "";
  }
};

const getFoodOptionMessage = (
  foodOption: FSVFoodPackages | undefined,
  intl: IntlShape,
): string => {
  switch (foodOption) {
    case "catering_packages":
    case "catering_packages_available":
      return intl.formatMessage({
        defaultMessage: "Select from catering packages",
      });
    case "full_menu_a_la_carte":
      return intl.formatMessage({
        defaultMessage: "Guests can order à la carte",
      });
    case "outside_food_allowed":
      return intl.formatMessage({
        defaultMessage: "Outside food allowed",
      });
    case "no_food":
      return intl.formatMessage({
        defaultMessage: "Food not included",
      });
    default:
      return "";
  }
};

const getRoomTypeMessage = (
  roomType: FSVRoomTypes,
  intl: IntlShape,
): string => {
  switch (roomType) {
    case "full_space_buyout":
      return intl.formatMessage({
        defaultMessage: "Full space",
      });
    case "private_room":
      return intl.formatMessage({
        defaultMessage: "Private room",
      });
    case "semi_private_area":
      return intl.formatMessage({
        defaultMessage: "Semi-private area",
      });
    case "reserved_area":
      return intl.formatMessage({
        defaultMessage: "Reserved area",
      });
    default:
      return "";
  }
};

const formatFoodAndDrinkCategoryTerm = (intl: IntlShape) =>
  intl.formatMessage({ defaultMessage: "food and drink" });

/**
 * Lowercase term for FSV minimum copy, from designer food/drink label matrix (`getFoodDrinkLabel`).
 * Falls back to “food and drink” when inputs are incomplete or the matrix adds a new label before this switch is updated.
 */
const getFoodAndDrinkMessage = (
  drinkOption: FSVDrinkOptions | undefined,
  foodOption: FSVFoodPackages | undefined,
  intl: IntlShape,
): string => {
  const label = getFoodDrinkLabel(foodOption, drinkOption);
  switch (label) {
    case "food_and_drink_min":
    case "food_and_drink":
      return formatFoodAndDrinkCategoryTerm(intl);
    case "drink_options_only":
      return intl.formatMessage({ defaultMessage: "drink" });
    case "food_min_only":
      return intl.formatMessage({ defaultMessage: "food" });
    default:
      return formatFoodAndDrinkCategoryTerm(intl);
  }
};

const _foodSubCopy = (foodOption: FSVFoodPackages, intl: IntlShape) => {
  switch (foodOption) {
    case "catering_packages":
    case "catering_packages_available":
      return intl.formatMessage({
        defaultMessage: "catering packages",
      });
    case "full_menu_a_la_carte":
      return intl.formatMessage({
        defaultMessage: "à la carte menu",
      });
    case "outside_food_allowed":
      return intl.formatMessage({
        defaultMessage: "outside food allowed",
      });
    case "no_food":
      return intl.formatMessage({
        defaultMessage: "Food not included",
      });
    default:
      return assertNever(foodOption);
  }
};

const _drinkSubCopy = (drinkOption: FSVDrinkOptions, intl: IntlShape) => {
  switch (drinkOption) {
    case "full_bar":
      return intl.formatMessage({
        defaultMessage: "Full bar",
      });
    case "beer_and_wine_only":
      return intl.formatMessage({
        defaultMessage: "Beer, wine,",
      });
    case "non_alcoholic_options_only":
      return intl.formatMessage({
        defaultMessage: "Non-alcoholic drinks",
      });
    case "outside_drinks_allowed":
      return "";
    default:
      return assertNever(drinkOption);
  }
};

const _foodServiceOfferedCopy = (
  foodOption: FSVFoodPackages,
  intl: IntlShape,
) => {
  switch (foodOption) {
    case "catering_packages":
    case "catering_packages_available":
      return intl.formatMessage({
        defaultMessage: "Catering packages",
      });
    case "full_menu_a_la_carte":
      return intl.formatMessage({
        defaultMessage: "À la carte menu",
      });
    case "outside_food_allowed":
    case "no_food":
      return "";
    default:
      return assertNever(foodOption);
  }
};

const _drinkServiceOfferedCopy = (
  drinkOption: FSVDrinkOptions,
  intl: IntlShape,
) => {
  switch (drinkOption) {
    case "full_bar":
      return intl.formatMessage({
        defaultMessage: "Full bar service",
      });
    case "beer_and_wine_only":
      return intl.formatMessage({
        defaultMessage: "Beer and wine menu",
      });
    case "non_alcoholic_options_only":
      return intl.formatMessage({
        defaultMessage: "Non-alcoholic drink menu",
      });
    case "outside_drinks_allowed":
      return intl.formatMessage({
        defaultMessage: "Outside drinks allowed",
      });
    default:
      return assertNever(drinkOption);
  }
};

const foodAndDrinkOptions = (
  drinkOptions: FSVDrinkOptions | undefined,
  foodPackages: FSVFoodPackages | undefined,
  intl: IntlShape,
) => {
  const label = getFoodDrinkLabel(foodPackages, drinkOptions);

  switch (label) {
    case "food_and_drink":
      return intl.formatMessage(
        {
          defaultMessage: "{drinkOption} and {foodOption}",
        },
        {
          foodOption: _foodSubCopy(foodPackages as FSVFoodPackages, intl),
          drinkOption: _drinkSubCopy(drinkOptions as FSVDrinkOptions, intl),
        },
      );
    case "food_and_drink_min":
      return intl.formatMessage({
        defaultMessage: "Outside food and drinks allowed",
      });
    case "drink_options_only":
      return _drinkServiceOfferedCopy(drinkOptions as FSVDrinkOptions, intl);
    case "food_min_only":
      return _foodServiceOfferedCopy(foodPackages as FSVFoodPackages, intl);
    default:
      return intl.formatMessage({ defaultMessage: "Food and drink" });
  }
};

export type { FoodDrinkLabel } from "./fsv-food-drink-category.utils";
export { getFoodDrinkLabel } from "./fsv-food-drink-category.utils";

export {
  getDrinkOptionMessage,
  getFoodAndDrinkMessage,
  getFoodOptionMessage,
  getRoomTypeMessage,
  getRoomTypeWithDurationMessage,
  foodAndDrinkOptions,
};
